import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  Home,
  Package2,
  ShoppingCart,
  ClipboardList,
  User,
  LayoutDashboard,
  Users,
  Settings
} from 'lucide-react';

interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  adminOnly?: boolean;
}

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  
  const isAdmin = user?.isAdmin;
  const isCurrentPath = (path: string) => location.pathname === path;

  // User Navigation Tabs
  const userTabs: TabItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: <Home className="w-5 h-5" />,
      path: '/'
    },
    {
      id: 'products',
      label: 'Products',
      icon: <Package2 className="w-5 h-5" />,
      path: '/products'
    },
    {
      id: 'cart',
      label: 'Cart',
      icon: <ShoppingCart className="w-5 h-5" />,
      path: '/cart',
      badge: itemCount > 0 ? itemCount : undefined
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: <ClipboardList className="w-5 h-5" />,
      path: isAuthenticated ? '/orders' : '/login'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <User className="w-5 h-5" />,
      path: isAuthenticated ? '/profile' : '/login'
    }
  ];

  // Admin Navigation Tabs - Admin-focused mobile experience
  const adminTabs: TabItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/admin/dashboard',
      adminOnly: true
    },
    {
      id: 'admin-products',
      label: 'Products',
      icon: <Package2 className="w-5 h-5" />,
      path: '/admin/products',
      adminOnly: true
    },
    {
      id: 'admin-orders',
      label: 'Orders',
      icon: <ClipboardList className="w-5 h-5" />,
      path: '/admin/orders',
      adminOnly: true
    },
    {
      id: 'admin-users',
      label: 'Users',
      icon: <Users className="w-5 h-5" />,
      path: '/admin/users',
      adminOnly: true
    },
    {
      id: 'admin-settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      path: '/admin/payment-gateways',
      adminOnly: true
    }
  ];

  // Choose tabs based on user role
  const currentTabs = isAdmin ? adminTabs : userTabs;

  // Hide bottom nav only on checkout pages to avoid distractions during payment
  const hideOnPages = ['/checkout'];
  if (hideOnPages.some(page => location.pathname.startsWith(page))) {
    return null;
  }

  const handleTabClick = (tab: TabItem) => {
    navigate(tab.path);
  };

  const getTabClasses = (tab: TabItem) => {
    let isActive = isCurrentPath(tab.path);
    
    // Special handling for specific routes
    if (tab.id === 'home' && location.pathname === '/') {
      isActive = true;
    } else if (tab.id === 'dashboard' && location.pathname === '/admin/dashboard') {
      isActive = true;
    } else if (tab.id === 'admin-products' && location.pathname === '/admin/products') {
      isActive = true;
    } else if (tab.id === 'admin-orders' && location.pathname === '/admin/orders') {
      isActive = true;
    } else if (tab.id === 'admin-users' && location.pathname === '/admin/users') {
      isActive = true;
    } else if (tab.id === 'admin-settings' && (
      location.pathname === '/admin/payment-gateways' || 
      location.pathname === '/admin/shipping'
    )) {
      isActive = true;
    }

    return `
      flex flex-col items-center justify-center px-2 py-2 rounded-lg transition-all duration-200 relative min-w-14
      ${isActive 
        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20' 
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-secondary-700'
      }
    `;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 block sm:hidden">
      {/* Bottom Navigation Container */}
      <div className="bg-white dark:bg-secondary-800 border-t border-gray-200 dark:border-secondary-700 shadow-lg">
        <div className="flex items-center justify-around px-2 py-1 max-w-screen-sm mx-auto">
          {currentTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              className={getTabClasses(tab)}
            >
              {/* Icon with Badge */}
              <div className="relative">
                {tab.icon}
                {tab.badge && tab.badge > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-5 font-medium">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </div>
              
              {/* Label */}
              <span className="text-xs mt-1 font-medium truncate max-w-16">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
        
        {/* Safe area for iOS */}
        <div className="h-0 sm:h-0 pb-safe bg-white dark:bg-secondary-800"></div>
      </div>
    </div>
  );
};

export default BottomNavigation;